import React, {
  ForwardedRef,
  forwardRef,
  RefObject,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from "react"
import {useEnvironment} from "@webng/react-app-common";
import {fireTickarooLiveblogInitEvent, fireTickarooLiveblogTracking} from "../events";
import {
  DefaultConsentProvider,
  Liveblog,
  LiveblogEvent,
  LiveblogRenderContext,
  LiveblogRenderContextProvider,
  LiveblogRenderPortals,
  LiveblogWrapper,
  TrackingFunction
} from "@webng/liveblog";
import { SortOrder, buildAnalyticsThemeConfiguration } from "@webng-types/embedjs";
import {LiveblogCoreApi, LiveblogCoreWidgetProps, NavigateToEventIdOptions,} from "./LiveblogCoreWidgetProps";
import * as externalUserProvider from '../user'
import {useLiveblogAnalyticsAgent} from "./analytics";
import {useLiveblogData} from "./useLiveblogData";
import {IEvent, IMedia, ITag} from "@webng-types/write-model";
import {LoadErrorView} from "../LoadErrorView";
import DpaTracking from "./DpaTracking";
import {CustomConsentProvider} from "./CustomConsentProvider";
import {useAdExtension} from "../../ads/useAdExtension";
import {AdManager} from "../../ads/AdManager";
import {scrollToElement} from "../../utils/scrollToElement";
import {useHeaderOffset} from "../useHeaderOffset";
import { useReactions } from "@webng/liveblog/src/reactions/useReactions";


type LiveblogCoreControllerProps = Omit<LiveblogCoreWidgetProps, "locale" | "clientId">

const DPA_PRODUCTION_ORGA_ID = '58514026e4b08f124d8e26e6';
const DPA_STAGING_ORGA_ID = '583303a3e4b0085862630479';

function apiCanNotLoadMore(): Promise<void> {
  return Promise.reject("No more data");
}

function LiveblogCoreWidgetImp({ liveblogId, customerConsent, css, eventId, initialData, disableTracking, liveblogThemeConfiguration, sharedThemeConfiguration, themeId, relativeLinkBaseUrl }: LiveblogCoreControllerProps, ref: ForwardedRef<LiveblogCoreApi>) {
  const agent = useLiveblogAnalyticsAgent(initialData.gameShowResponse?.game?.ticker_id ?? liveblogId, sharedThemeConfiguration.useCookies === 'all', disableTracking);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const env = useEnvironment();
  const [adManager, setAdManager] = useState<AdManager>()
  const [currentSort, setCurrentSort] = useState<SortOrder>(liveblogThemeConfiguration.sort)

  // Held in a ref so the once-per-load t_ini effect can read the resolved theme without taking the
  // config objects as effect dependencies (which would re-fire the load event).
  const analyticsTheme = useMemo(
    () => buildAnalyticsThemeConfiguration(liveblogThemeConfiguration, sharedThemeConfiguration, themeId ?? ""),
    [liveblogThemeConfiguration, sharedThemeConfiguration, themeId]
  )
  const analyticsThemeRef = useRef(analyticsTheme)
  analyticsThemeRef.current = analyticsTheme

  const trackingFunction: TrackingFunction = useCallback((e: LiveblogEvent) => {
    if(agent) {
      agent.track(e)
      if (wrapperRef.current && wrapperRef.current.parentElement) {
        fireTickarooLiveblogTracking(wrapperRef.current.parentElement, e)
      }
    }
  }, [agent])

  const [portals, setPortals] = useState<LiveblogRenderPortals>({})

  const {
    error,
    game,
    events,
    rows,
    pendingUpdate,
    milestones,
    goals,
    highlights,
    loadMoreBottom,
    loadMoreTop,
    backToTop,
    navigateToEventId,
    selectedTags,
    setSelectedTags,
    applyPendingUpdate,
    isLoadingBackToLive,
    isLoadingMoreTop,
    isLoadingMoreBottom,
    isLoadingPendingUpdate,
    isLoadingSortChange
  } = useLiveblogData({
    id: liveblogId,
    limit: liveblogThemeConfiguration.limit,
    deepLinkLimit: liveblogThemeConfiguration.deepLinkLimit,
    deepLinkDepth: liveblogThemeConfiguration.deepLinkDepth,
    trackingFunction,
    refresh: liveblogThemeConfiguration.refresh,
    reverse: currentSort === 'asc' as SortOrder,
    initialData: initialData.gameShowResponse,
    isSSR: initialData.isSSR
  })

  const { reactionCounts } = useReactions(Object.keys(events), !!game.reaction_set_id);

  const navigateToEventIdWrap = useCallback((eventId: string | undefined, options?: NavigateToEventIdOptions) => {
    if (eventId) {
      const existingElement = wrapperRef.current?.querySelector(`[data-tickaroo-event-id="${eventId}"]`);
      if (existingElement) {
        scrollToElement(existingElement, options);
        return Promise.resolve(true);
      } else {
        return navigateToEventId(eventId).then(async _ => {
          const pollForElement = (): Promise<Element | null> => {
            return new Promise((resolve) => {
              const interval = 50;
              // generous: rendering a freshly-loaded window of full-content posts can take a
              // while on slower devices/connections, and the target must be committed to the DOM
              // before we can scroll to it. A short timeout here silently dropped the scroll.
              const timeout = 12000;
              const startTime = Date.now();

              const checkElement = () => {
                const element = wrapperRef.current?.querySelector(`[data-tickaroo-event-id="${eventId}"]`);
                if (element) {
                  resolve(element);
                } else if (Date.now() - startTime > timeout) {
                  resolve(null);
                } else {
                  setTimeout(checkElement, interval);
                }
              };

              checkElement();
            });
          };

          const element = await pollForElement();
          if (element) {
            scrollToElement(element, options);
            // Re-apply after the just-loaded strip settles (card widths, the newer/back-to-live
            // lead control, any scroll reset): a single fire-and-forget scroll can land before
            // layout is final and leave the jump half-applied. Idempotent once the target is in
            // view, so fast environments are unaffected.
            const reconfirm = () => {
              const elNow = wrapperRef.current?.querySelector(`[data-tickaroo-event-id="${eventId}"]`);
              if (elNow) scrollToElement(elNow, options);
            };
            setTimeout(reconfirm, 300);
            setTimeout(reconfirm, 800);
            return true;
          } else {
            return false;
          }
        });
      }
    } else {
      return navigateToEventId(eventId).then(_ => true);
    }
  }, [navigateToEventId]);

  const useFunctionalCookies = sharedThemeConfiguration.useCookies === 'all' || sharedThemeConfiguration.useCookies === 'functional'

  // the provider must stay stable across data loads and re-renders, otherwise consent state is lost
  const consentProvider = useMemo(() => {
    return customerConsent ? new CustomConsentProvider(useFunctionalCookies, wrapperRef as RefObject<HTMLDivElement>) : new DefaultConsentProvider(useFunctionalCookies)
  }, [customerConsent, useFunctionalCookies])

  const renderContext: Partial<LiveblogRenderContext> = useMemo(() => {
    return {
      consentProvider,
      webEmbedDefaultProviderConstraint: liveblogThemeConfiguration.webEmbedDefaultConstraint,
      webEmbedProviderConstraints: Object.assign({}, liveblogThemeConfiguration.webEmbedConstraints, { Website: 'full' }),
      showEventSharing: liveblogThemeConfiguration.sharing,
      sharingLinkTarget: liveblogThemeConfiguration.sharingLinkTarget,
      layoutAuthorPosition: liveblogThemeConfiguration.layoutAuthorPosition,
      commentsMode: liveblogThemeConfiguration.commentsMode,
      dateTimeDisplay: liveblogThemeConfiguration.dateTimeDisplay,
      externalUserProvider: externalUserProvider,
      uniqueId: initialData.uniqueId,
      // The one thing this widget cannot work out for itself: the same bundle serves publisher embeds
      // and Tickaroo's hosted fullpage, and Viafoura resolves the account from the page's hostname.
      // The server tells us which one this is (design D22). Deliberately not derived from
      // location.hostname — the gating spec wants the render surface, not a domain check, and a host
      // allowlist would have to track staging, production and preview hosts forever.
      viafouraInteractive: !initialData.tickarooHosted,
      useFunctionalCookies,
      trackingFunction,
      portals,
      relativeLinkBaseUrl,
      navigateToEventId: navigateToEventIdWrap,
      colorScheme: sharedThemeConfiguration.colorScheme
    }
  }, [liveblogThemeConfiguration.webEmbedDefaultConstraint, liveblogThemeConfiguration.webEmbedConstraints, liveblogThemeConfiguration.commentsMode, liveblogThemeConfiguration.dateTimeDisplay, liveblogThemeConfiguration.sharing, liveblogThemeConfiguration.sharingLinkTarget, liveblogThemeConfiguration.layoutAuthorPosition, initialData.uniqueId, initialData.tickarooHosted, consentProvider, useFunctionalCookies, trackingFunction, portals, relativeLinkBaseUrl, navigateToEventIdWrap, sharedThemeConfiguration.colorScheme]);

  // provide imperative methods on the liveblog element
  useImperativeHandle<LiveblogCoreApi, LiveblogCoreApi>(ref, () => ({
    loadMoreTop: loadMoreTop || apiCanNotLoadMore,
    loadMoreBottom: loadMoreBottom || apiCanNotLoadMore,
    navigateToEventId: navigateToEventIdWrap,
    hasEventIdLoaded: function (eventId: string) {
      return !!rows.find(r => r.local_id === eventId)
    },
    setConsent: (provider: string, consent: boolean): void => {
      renderContext.consentProvider?.setConsent(provider, consent)
    },
    setAdManager,
    getCurrentLiveblogData: () => {
      return {
        game,
        events,
        pendingUpdate
      }
    },
    registerPortalForType: (type, portal) => {
      setPortals(portals => ({
        ...portals,
        [type]: [...(portals[type] || []), portal]
      }))
    },
    unregisterPortalForType: (type, portal) => {
      setPortals(portals => ({
        ...portals,
        [type]: portals[type]?.filter(existingPortal => existingPortal.element !== portal) || []
      }))
    }
  }), [loadMoreTop, loadMoreBottom, navigateToEventIdWrap, rows, renderContext.consentProvider, game, events, pendingUpdate])

  // dispatch init event
  useEffect(() => {
    if (wrapperRef.current && wrapperRef.current.parentElement) {
      fireTickarooLiveblogInitEvent(wrapperRef.current.parentElement, { liveblogId: liveblogId })
    }
  }, [liveblogId])

  // initialize tracking if game is loaded
  const shouldStartAnalytics = !!game && error === undefined
  useEffect(() => {
    if(agent && shouldStartAnalytics) {
      agent?.start()
      trackingFunction({t: 't_ini', th: analyticsThemeRef.current})

      return () => {
        agent?.stop(true)
      }
    }
  }, [trackingFunction, agent, shouldStartAnalytics])

  // react to eventId
  useEffect(() => {
    if (eventId) {
      navigateToEventIdWrap(eventId)
    }
    // navigateToEventIdWrap is not in the dependencies list as it would cause
    // this callback to be called to often. we only want to call it for the initial eventId jump
    // eslint-disable-next-line
  }, [eventId])

  useEffect(() => {
    setCurrentSort(liveblogThemeConfiguration.sort)
  }, [liveblogThemeConfiguration.sort]);

  const onMilestoneClick = useCallback((milestone: IEvent) => {
    trackingFunction({ t: 't_mil', e: milestone.local_id })
    navigateToEventIdWrap(milestone.local_id)
  }, [navigateToEventIdWrap, trackingFunction])

  const onHighlightClick = useCallback((highlight: IEvent) => {
    trackingFunction({ t: 't_hlt', e: highlight.local_id })
    navigateToEventIdWrap(highlight.local_id)
  }, [navigateToEventIdWrap, trackingFunction])

  const onGameSummaryClick = useCallback((summary: IEvent) => {
    trackingFunction({ t: 't_sgs', e: summary.local_id })
    navigateToEventIdWrap(summary.local_id)
  }, [navigateToEventIdWrap, trackingFunction])

  const onSortChange = useCallback((newSort: SortOrder) => {
    trackingFunction({ t: 't_srt', id: newSort})
    setCurrentSort(newSort)
  }, [setCurrentSort, trackingFunction])

  const onChangeTags = useCallback((tags: ITag[]|undefined) => {
    trackingFunction({ t: 't_tag', url: `#tickaroo_tags=${tags?.map(t => t._id).join(',') || ''}` })
    if(tags === undefined || tags.length === 0) {
      setSelectedTags(undefined)
    } else {
      setSelectedTags(tags.map(t => t._id!))
    }
  }, [setSelectedTags, trackingFunction])

  const onMediaClick = useCallback((media: IMedia) => {
    trackingFunction({ t: 's_opn', m: media.local_id })
    import(/* webpackChunkName: "slideshow" */ '../../slideshow/slideshow').then(m => m.startSlideshow(rows, media.local_id, trackingFunction, env.apiHost))
  }, [rows, trackingFunction, env.apiHost])


  const onApplyPendingUpdates = useCallback(() => {
    applyPendingUpdate();
    setTimeout(() => wrapperRef.current && scrollToElement(wrapperRef.current), 10);
  }, [applyPendingUpdate, wrapperRef])

  const headerOffset = useHeaderOffset(!!pendingUpdate)

  const backToLiveLink = backToTop ? "#" : undefined
  const loadMoreTopLink = loadMoreTop ? "#" : undefined
  const loadMoreBottomLink = loadMoreBottom ? "#" : undefined

  const includeDpaTracking = !!game.upstreams?.find(u => u._type === "Tik::Model::Upstream::MarketplaceUpstream" && u.seller && (u.seller._id === DPA_PRODUCTION_ORGA_ID || u.seller._id === DPA_STAGING_ORGA_ID));

  const extension = useAdExtension(adManager, rows, game)

  if (error !== undefined) {
    return <LoadErrorView status={error} />
  } else {
    return <>
      <LiveblogRenderContextProvider context={renderContext}>
        <LiveblogWrapper
          ref={wrapperRef}
          liveblogId={liveblogId}
          sharing={liveblogThemeConfiguration.sharing}
          invertHighlightEvents={liveblogThemeConfiguration.styleInvertHighlightEvents}
          invertStickyEvents={liveblogThemeConfiguration.styleInvertStickyEvents}
          invertSummaryEvents={liveblogThemeConfiguration.styleInvertSummaryEvents}
          invertTime={liveblogThemeConfiguration.styleInvertTime}
          dateTimeDisplay={liveblogThemeConfiguration.dateTimeDisplay}
          colorScheme={sharedThemeConfiguration.colorScheme}
          layout={liveblogThemeConfiguration.layout}
          layoutAuthorPosition={liveblogThemeConfiguration.layoutAuthorPosition}
          layoutEventTagPosition={liveblogThemeConfiguration.layoutEventTagPosition}
          styleUseCards={liveblogThemeConfiguration.styleUseCards}
          stylePrimaryColor={liveblogThemeConfiguration.stylePrimaryColor}
          stylePrimaryColorDark={liveblogThemeConfiguration.stylePrimaryColorDark}
          styleSecondaryColor={liveblogThemeConfiguration.styleSecondaryColor}
          styleSecondaryColorDark={liveblogThemeConfiguration.styleSecondaryColorDark}
          styleTextColor={liveblogThemeConfiguration.styleTextColor}
          styleTextColorDark={liveblogThemeConfiguration.styleTextColorDark}
          styleTextMutedColor={liveblogThemeConfiguration.styleTextMutedColor}
          styleTextMutedColorDark={liveblogThemeConfiguration.styleTextMutedColorDark}
          styleBgColor={liveblogThemeConfiguration.styleBgColor}
          styleBgColorDark={liveblogThemeConfiguration.styleBgColorDark}
          styleEventHighlightBgColor={liveblogThemeConfiguration.styleEventHighlightBgColor}
          styleEventHighlightBgColorDark={liveblogThemeConfiguration.styleEventHighlightBgColorDark}
          styleEventStickyBgColor={liveblogThemeConfiguration.styleEventStickyBgColor}
          styleEventStickyBgColorDark={liveblogThemeConfiguration.styleEventStickyBgColorDark}
          styleEventSummaryBgColor={liveblogThemeConfiguration.styleEventSummaryBgColor}
          styleEventSummaryBgColorDark={liveblogThemeConfiguration.styleEventSummaryBgColorDark}
          headerOffset={headerOffset}
          css={css}
          reactionsEnabled={game.reaction_set_id !== undefined}
          >
          <Liveblog
            game={game}
            milestones={milestones}
            goals={goals}
            highlights={highlights}
            rows={rows}
            selectedTags={selectedTags}
            pendingUpdate={pendingUpdate}
            autoRefresh={liveblogThemeConfiguration.refresh === 'auto'}
            onChangeTags={onChangeTags}
            onMilestoneClick={onMilestoneClick}
            onHighlightClick={onHighlightClick}
            onGameSummaryClick={onGameSummaryClick}
            onMediaClick={liveblogThemeConfiguration.useSlideshow ? onMediaClick : undefined}
            onBackToLiveClick={backToTop}
            onLoadMoreTopClick={loadMoreTop}
            onLoadMoreBottomClick={loadMoreBottom}
            onApplyPendingUpdates={onApplyPendingUpdates}
            onSortChange={onSortChange}
            backToLiveLink={backToLiveLink}
            loadMoreTopLink={loadMoreTopLink}
            loadMoreBottomLink={loadMoreBottomLink}
            isLoadingBackToLive={isLoadingBackToLive}
            isLoadingMoreTop={isLoadingMoreTop}
            isLoadingMoreBottom={isLoadingMoreBottom}
            isLoadingPendingUpdate={isLoadingPendingUpdate}
            isLoadingSortChange={isLoadingSortChange}
            highlightSummaryType={liveblogThemeConfiguration.highlightSummaryType}
            sportsShowScoreboard={liveblogThemeConfiguration.sportsShowScoreboard}
            sportsShowScorerList={liveblogThemeConfiguration.sportsShowScorerList}
            automaticSummaryHighlightsLimit={liveblogThemeConfiguration.automaticSummaryHighlightsLimit}
            eventTagNavigation={liveblogThemeConfiguration.eventTagNavigation}
            extension={extension}
            reactionCounts={reactionCounts}
            sortControls={liveblogThemeConfiguration.sortControls}
            currentSort={currentSort}
          />
        </LiveblogWrapper>
      </LiveblogRenderContextProvider>
      {includeDpaTracking && <DpaTracking />}
    </>
  }
}

export const LiveblogCoreWidget = forwardRef<LiveblogCoreApi, LiveblogCoreControllerProps>(LiveblogCoreWidgetImp)
